import { Component, OnDestroy, computed, input, linkedSignal, output, signal } from '@angular/core';
import { outputFromObservable, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs';
import moment from 'moment';
import { APP } from 'src/app/config/app';
import { IPlaybackRange, IPlaybackStart, IPlayerTick } from 'src/app/models';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';

@Component({
  selector: 'app-positions-player',
  templateUrl: './positions-player.component.html',
  styleUrl: './positions-player.component.scss',
  standalone: false,
  host: {
    '(document:keydown.space)': 'onSpaceKey($event)',
    '(document:keydown.arrowleft)': 'onArrowKey($event, -1)',
    '(document:keydown.arrowright)': 'onArrowKey($event, 1)',
  },
})
export class PositionsPlayerComponent implements OnDestroy {
  readonly selectedDay = input<Date>();
  readonly minDay = input<Date>();
  readonly maxDay = input<Date>();
  readonly daySelectable = input(true);
  readonly tripRange = input<IPlaybackRange>();
  readonly shortcutsEnabled = input(true);
  readonly dayChange = output<Date>();

  readonly images = images;
  readonly speeds = APP.POSITIONS_PLAYER_SPEEDS;
  private readonly hourTicks = this.buildHourTicks();

  readonly now = signal(new Date());
  readonly day = linkedSignal({
    source: this.selectedDay,
    computation: (selectedDay) => this.startOfDay(selectedDay ?? new Date()),
    equal: (previous, current) => previous.getTime() === current.getTime(),
  });
  private readonly playbackStart = computed(() => ({ day: this.day(), range: this.tripRange() }));
  readonly seconds = linkedSignal<IPlaybackStart, number>({
    source: this.playbackStart,
    computation: ({ day, range }, previous) => {
      if (range) return range.startMinute * APP.SECONDS_PER_MINUTE;
      if (previous && previous.source.day.getTime() === day.getTime()) return previous.value;
      return this.isSameDay(day, new Date()) ? this.secondsOfDay(new Date()) : 0;
    },
  });
  readonly playing = linkedSignal<IPlaybackStart, boolean>({
    source: this.playbackStart,
    computation: ({ day, range }, previous) => {
      if (range) return false;
      if (previous && previous.source.day.getTime() === day.getTime()) return previous.value;
      return this.isSameDay(day, new Date());
    },
  });
  readonly speed = signal(APP.POSITIONS_PLAYER_SPEEDS[0]);

  readonly isToday = computed(() => this.isSameDay(this.day(), this.now()));
  readonly calendarDay = computed(() => moment(this.day()).format(APP.POSITIONS_DAY_FORMAT));
  readonly calendarMinDay = computed(() => this.calendarLimit(this.minDay()));
  readonly calendarMaxDay = computed(() => this.calendarLimit(this.maxDay() ?? this.now()));
  readonly canGoPrevious = computed(() => {
    const minDay = this.minDay();
    return !minDay || this.day().getTime() > this.startOfDay(minDay).getTime();
  });
  readonly canGoNext = computed(() => {
    const maxDay = this.maxDay();
    return !this.isToday() && (!maxDay || this.day().getTime() < this.startOfDay(maxDay).getTime());
  });
  readonly rangeStartMinute = computed(() => this.tripRange()?.startMinute ?? 0);
  readonly rangeEndMinute = computed(() => this.tripRange()?.endMinute ?? APP.MINUTES_PER_DAY);
  private readonly rangeMinutes = computed(() => Math.max(this.rangeEndMinute() - this.rangeStartMinute(), 1));
  readonly ticks = computed(() => {
    const range = this.tripRange();
    return range ? this.buildTripTicks(range) : this.hourTicks;
  });
  private readonly minSeconds = computed(() => this.rangeStartMinute() * APP.SECONDS_PER_MINUTE);
  readonly maxSeconds = computed(() => {
    const rangeEndSeconds = this.rangeEndMinute() * APP.SECONDS_PER_MINUTE;
    return this.isToday() ? Math.min(this.secondsOfDay(this.now()), rangeEndSeconds) : rangeEndSeconds;
  });
  readonly currentMinute = computed(() => Math.floor(this.seconds() / APP.SECONDS_PER_MINUTE));
  readonly progressFraction = computed(() => this.rangeFraction(this.currentMinute()));
  readonly atNow = computed(
    () =>
      this.isToday() &&
      this.currentMinute() === Math.floor(this.secondsOfDay(this.now()) / APP.SECONDS_PER_MINUTE),
  );
  readonly availableFraction = computed(() =>
    this.isToday() ? this.rangeFraction(this.secondsOfDay(this.now()) / APP.SECONDS_PER_MINUTE) : 1,
  );
  readonly currentTime = computed(() => {
    const time = new Date(this.day());
    time.setMinutes(this.currentMinute());
    return time;
  });

  readonly timeChange = outputFromObservable(
    toObservable(this.currentTime).pipe(
      distinctUntilChanged((previous, current) => previous.getTime() === current.getTime()),
    ),
  );

  private lastTick = Date.now();
  private readonly ticker = setInterval(() => this.tick(), APP.POSITIONS_PLAYER_TICK_MS);

  constructor(public literalService: LiteralService) {}

  previousDay() {
    if (!this.canGoPrevious()) return;
    this.changeDay(-1);
  }

  nextDay() {
    if (!this.canGoNext()) return;
    this.changeDay(1);
  }

  togglePlay() {
    if (!this.playing() && !this.isToday() && this.seconds() >= this.maxSeconds()) {
      this.setSeconds(this.minSeconds());
    }
    this.playing.update((playing) => !playing);
  }

  selectCalendarDay(event: Event) {
    const dayInput = event.target as HTMLInputElement | null;
    const selected = moment(dayInput?.value, APP.POSITIONS_CALENDAR_DAY_FORMAT);
    const typedDay = selected.isValid() ? this.startOfDay(selected.toDate()) : undefined;
    const lastSelectableDay = this.lastSelectableDay();
    const day = typedDay && typedDay.getTime() > lastSelectableDay.getTime() ? lastSelectableDay : typedDay;
    const acceptedDay = day && this.isOnOrAfterMinDay(day) ? day : undefined;
    if (dayInput) dayInput.value = moment(acceptedDay ?? this.day()).format(APP.POSITIONS_CALENDAR_DAY_FORMAT);
    if (!acceptedDay || acceptedDay.getTime() === this.day().getTime()) return;
    this.day.set(acceptedDay);
    this.dayChange.emit(acceptedDay);
  }

  private lastSelectableDay(): Date {
    return this.startOfDay(this.maxDay() ?? this.now());
  }

  private isOnOrAfterMinDay(day: Date): boolean {
    const minDay = this.minDay();
    return !minDay || day.getTime() >= this.startOfDay(minDay).getTime();
  }

  onSpaceKey(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (!this.shortcutsEnabled()) return;
    if (keyboardEvent.repeat || this.targetMatches(keyboardEvent, APP.POSITIONS_PLAYER_SPACE_IGNORED_TARGETS)) return;
    keyboardEvent.preventDefault();
    this.togglePlay();
  }

  onArrowKey(event: Event, direction: number) {
    if (!this.shortcutsEnabled() || this.targetMatches(event, APP.POSITIONS_PLAYER_ARROW_IGNORED_TARGETS)) return;
    event.preventDefault();
    const step = APP.POSITIONS_PLAYER_KEYBOARD_STEP_MINUTES;
    const minute = this.currentMinute();
    const targetMinute = direction > 0 ? (Math.floor(minute / step) + 1) * step : (Math.ceil(minute / step) - 1) * step;
    this.setSeconds(targetMinute * APP.SECONDS_PER_MINUTE);
  }

  private targetMatches(event: Event, selector: string): boolean {
    return !!(event.target as HTMLElement | null)?.closest?.(selector);
  }

  seek(event: Event) {
    const input = event.target as HTMLInputElement;
    this.setSeconds(Number(input.value) * APP.SECONDS_PER_MINUTE);
    input.value = String(this.currentMinute());
  }

  setTime(event: Event) {
    const input = event.target as HTMLInputElement;
    const [hours, minutes] = input.value.split(':').map(Number);
    if (input.value) this.setSeconds((hours * APP.MINUTES_PER_HOUR + minutes) * APP.SECONDS_PER_MINUTE);
    input.value = this.formatTime(this.currentTime());
  }

  ngOnDestroy(): void {
    clearInterval(this.ticker);
  }

  private tick() {
    const nowMs = Date.now();
    const elapsedSeconds = (nowMs - this.lastTick) / APP.MS_PER_SECOND;
    this.lastTick = nowMs;
    this.now.set(new Date(nowMs));
    if (!this.playing()) return;
    const next = this.seconds() + elapsedSeconds * this.speed() * APP.SECONDS_PER_MINUTE;
    if (next >= this.maxSeconds() && !this.isToday()) {
      this.playing.set(false);
    }
    this.setSeconds(next);
  }

  private changeDay(offset: number) {
    const day = new Date(this.day());
    day.setDate(day.getDate() + offset);
    this.day.set(day);
    this.dayChange.emit(day);
  }

  private setSeconds(value: number) {
    this.seconds.set(Math.min(Math.max(value, this.minSeconds()), this.maxSeconds()));
  }

  private rangeFraction(minute: number): number {
    return Math.min(Math.max((minute - this.rangeStartMinute()) / this.rangeMinutes(), 0), 1);
  }

  private calendarLimit(date?: Date): string | undefined {
    return date ? moment(date).format(APP.POSITIONS_CALENDAR_DAY_FORMAT) : undefined;
  }

  private formatTime(date: Date): string {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  private isSameDay(first: Date, second: Date): boolean {
    return this.startOfDay(first).getTime() === this.startOfDay(second).getTime();
  }

  private startOfDay(date: Date): Date {
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    return day;
  }

  private secondsOfDay(date: Date): number {
    return (
      (date.getHours() * APP.MINUTES_PER_HOUR + date.getMinutes()) * APP.SECONDS_PER_MINUTE +
      date.getSeconds() +
      date.getMilliseconds() / APP.MS_PER_SECOND
    );
  }

  private buildTripTicks(range: IPlaybackRange): IPlayerTick[] {
    const labelledMinutes = this.tripLabelMinutes(range);
    const ticksBetweenLabels = APP.POSITIONS_PLAYER_TRIP_TICKS_BETWEEN_LABELS;
    const ticks: IPlayerTick[] = [];
    labelledMinutes.forEach((minute, index) => {
      ticks.push({ label: this.formatMinute(minute), percent: this.rangeFractionOf(range, minute) * 100 });
      const nextMinute = labelledMinutes[index + 1];
      if (nextMinute === undefined) return;
      for (let tick = 1; tick <= ticksBetweenLabels; tick++) {
        const tickMinute = minute + ((nextMinute - minute) * tick) / (ticksBetweenLabels + 1);
        ticks.push({ label: null, percent: this.rangeFractionOf(range, tickMinute) * 100 });
      }
    });
    return ticks;
  }

  private tripLabelMinutes(range: IPlaybackRange): number[] {
    const minimumInnerLabels = APP.POSITIONS_PLAYER_TRIP_MIN_INNER_LABELS;
    const maximumInnerLabels = APP.POSITIONS_PLAYER_TRIP_MAX_INNER_LABELS;
    let bestInnerLabels: number[] | undefined;
    let bestDeviation = Infinity;
    for (let innerLabels = minimumInnerLabels; innerLabels <= maximumInnerLabels; innerLabels++) {
      const labels = this.evenlySpacedInnerLabels(range, innerLabels);
      if (labels.length < innerLabels) continue;
      const deviation = this.edgeGapDeviation(range, labels);
      if (deviation < bestDeviation) {
        bestDeviation = deviation;
        bestInnerLabels = labels;
      }
    }
    const inner = bestInnerLabels ?? this.evenlySpacedInnerLabels(range, minimumInnerLabels);
    return [range.startMinute, ...inner, range.endMinute];
  }

  private evenlySpacedInnerLabels(range: IPlaybackRange, innerLabels: number): number[] {
    const rounding = APP.POSITIONS_PLAYER_TRIP_LABEL_ROUNDING_MINUTES;
    const idealGap = (range.endMinute - range.startMinute) / (innerLabels + 1);
    const gap = Math.max(rounding, Math.round(idealGap / rounding) * rounding);
    const centeredStart = (range.startMinute + range.endMinute - gap * (innerLabels - 1)) / 2;
    const firstInner = Math.round(centeredStart / rounding) * rounding;
    return Array.from({ length: innerLabels }, (_, label) => firstInner + label * gap).filter(
      (minute) => minute > range.startMinute && minute < range.endMinute,
    );
  }

  private edgeGapDeviation(range: IPlaybackRange, innerLabels: number[]): number {
    const gap = innerLabels[1] - innerLabels[0];
    const firstEdge = innerLabels[0] - range.startMinute;
    const lastEdge = range.endMinute - innerLabels[innerLabels.length - 1];
    return Math.max(Math.abs(firstEdge - gap), Math.abs(lastEdge - gap)) / gap;
  }

  private rangeFractionOf(range: IPlaybackRange, minute: number): number {
    return (minute - range.startMinute) / Math.max(range.endMinute - range.startMinute, 1);
  }

  private formatMinute(minute: number): string {
    const hours = Math.floor(minute / APP.MINUTES_PER_HOUR);
    return `${String(hours).padStart(2, '0')}:${String(minute % APP.MINUTES_PER_HOUR).padStart(2, '0')}`;
  }

  private buildHourTicks(): IPlayerTick[] {
    const hoursPerDay = APP.SCHEDULE_ALL_HOURS.length;
    return [...APP.SCHEDULE_ALL_HOURS, hoursPerDay].map((hour) => ({
      label: hour % APP.POSITIONS_PLAYER_HOUR_LABEL_STEP === 0 ? `${String(hour).padStart(2, '0')}:00` : null,
      percent: (hour / hoursPerDay) * 100,
    }));
  }
}
